import { Injectable } from '@angular/core';
import { IS_DEVELOPMENT } from '@root/app.config';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { firstValueFrom } from 'rxjs';
import { PassDataService } from '@core/services/pass-data/pass-data.service';

// TODO: Should move all this to config files or pull from backend or something
const EXPERIMENT = '_geo_shop';
const VARIANTS = { control: 'control', experiment: 'shop' };
const CITY = 'adelaide';
const CITY_CODE = 'adl';
const ROLLOUT = 30; // % of eligible users to experimental variant
const ID_COOKIE = 'zt_bucket_id'; // just a random ID to keep people in the same bucket
const EXP_COOKIE = 'zt_exp_' + CITY_CODE + EXPERIMENT; // cookie to store experiment variant
const BUCKET_ITERATION = 1; // bump this to force re-bucketing // remember to match in edge worker

type Variant = (typeof VARIANTS)[keyof typeof VARIANTS];

@Injectable({ providedIn: 'root' })
export class VariantService {
	private variant: Variant;

	constructor(
		private serverDataService: ServerDataService,
		private passDataService: PassDataService,
	) {}

	private getCookie(name: string): string | null {
		const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
		return m ? decodeURIComponent(m[1]) : null;
	}

	// Default to 12 months
	private setCookie(name: string, value: string, maxAgeSec = 31536000) {
		const isHttps = location.protocol === 'https:';
		const parts = [
			`${name}=${encodeURIComponent(value)}`,
			'Path=/',
			`Max-Age=${maxAgeSec}`,
			'SameSite=Lax',
			isHttps ? 'Secure' : '', // browsers ignore Secure on http
		].filter(Boolean);
		document.cookie = parts.join('; ');
	}

	private deleteCookie(name: string) {
		document.cookie = `${name}=; Max-Age=0; Path=/;`;
	}

	private randomId() {
		const a = new Uint8Array(16);
		crypto.getRandomValues(a);
		return [...a].map((b) => b.toString(16).padStart(2, '0')).join('');
	}

	private async hashBucket(s: string) {
		const data = new TextEncoder().encode(s);
		const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', data));
		return hash[0] % 100; // 0..99
	}

	private stringifyExpCookie(bucketIter: number, variant: string) {
		return `b${bucketIter}:${variant}`;
	}

	private parseExpCookie<V extends string>(raw: string | null, allowed: readonly V[]): { bucketVersion: number; variant: V } | null {
		if (!raw) {
			return null;
		}

		const idx = raw.indexOf(':');
		if (idx <= 1) {
			return null;
		} // need at least 'bN:...'

		const verPart = raw.slice(0, idx); // 'b1'
		const variant = raw.slice(idx + 1); // dynamic, e.g. 'shop'
		const m = /^b(\d+)$/.exec(verPart);
		if (!m) {
			return null;
		} // bad version part

		if (!allowed.includes(variant as V)) {
			return null;
		} // bad variant part

		return { bucketVersion: Number(m[1]), variant: variant as V };
	}

	async init(): Promise<void> {
		if (!IS_DEVELOPMENT()) {
			this.variant = VARIANTS.control; // force 'control' variant in non-dev for now
			return Promise.resolve(); // avoid setting cookies in production yet
		}

		const url = new URL(window.location.href);
		const sessionKey = url.searchParams.get('');
		let shopVariantByServerFlag: Variant | null;

		if (url.searchParams.get('exp_clear') === '1') {
			this.deleteCookie(ID_COOKIE);
			this.deleteCookie(EXP_COOKIE);
			this.variant = VARIANTS.control;
			url.searchParams.delete('exp_clear');
			history.replaceState(null, '', url.toString());
		}

		if (sessionKey) {
			const countryCode = url.searchParams.get('country');
			await this.getSessionConfig(sessionKey, countryCode)
				.then((res) => {
					this.passDataService.sessionConfig = res;
					shopVariantByServerFlag = res.is_shop ? VARIANTS.experiment : null;
				})
				.catch((error) => console.error(error));
		}

		const forced = shopVariantByServerFlag || url.searchParams.get('exp_variant');
		if (forced === VARIANTS.control || forced === VARIANTS.experiment) {
			if (!this.getCookie(ID_COOKIE)) {
				this.setCookie(ID_COOKIE, this.randomId());
			}
			this.setCookie(EXP_COOKIE, this.stringifyExpCookie(BUCKET_ITERATION, forced));
			this.variant = forced;
			url.searchParams.delete('exp_variant');
			history.replaceState(null, '', url.toString());
		}

		const cityParam = (url.searchParams.get('exp_city') || '').toLowerCase();
		if (cityParam) {
			this.deleteCookie(ID_COOKIE);
			this.deleteCookie(EXP_COOKIE);
			const bucketId = this.randomId();
			this.setCookie(ID_COOKIE, bucketId);

			const inTargetCity = cityParam === CITY_CODE || cityParam === CITY;
			const bucket = await this.hashBucket(`${bucketId}|${EXPERIMENT}|b${BUCKET_ITERATION}`); // 0..99
			this.variant = inTargetCity && bucket < ROLLOUT ? VARIANTS.experiment : VARIANTS.control;
			this.setCookie(EXP_COOKIE, this.stringifyExpCookie(BUCKET_ITERATION, this.variant));

			url.searchParams.delete('exp_city');
			history.replaceState(null, '', url.toString());
		}

		if (!this.variant) {
			const rawExpCookie = this.getCookie(EXP_COOKIE);
			const parsedExpCookie = this.parseExpCookie(rawExpCookie, Object.values(VARIANTS));

			if (!parsedExpCookie || parsedExpCookie.bucketVersion !== BUCKET_ITERATION) {
				this.setCookie(EXP_COOKIE, this.stringifyExpCookie(BUCKET_ITERATION, VARIANTS.control));
				this.variant = VARIANTS.control;
			} else {
				this.variant = parsedExpCookie.variant;
			}

			if (!this.getCookie(ID_COOKIE)) {
				this.setCookie(ID_COOKIE, this.randomId());
			}
		}

		console.log('Experiment', { variant: this.variant, expCookie: this.getCookie(EXP_COOKIE), idCookie: this.getCookie(ID_COOKIE) });
		// Save the variant? probably not need as we always read the cookie
		return Promise.resolve();
	}

	isShop(): boolean {
		return this.variant === VARIANTS.experiment;
	}

	private async getSessionConfig<T extends { is_shop: boolean }>(sessionKey: string, countryCode: string): Promise<T> {
		return firstValueFrom(this.serverDataService.GetServerData(countryCode, sessionKey));
	}
}
